import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '../../../../core/config/api';

const PanelModerationV1: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = React.useState<Array<{
    id: number;
    username: string;
    argents?: number;
    medailles?: number;
    total_batiments: number;
    total_vehicules: number;
    total_personnel: number;
    total_interventions: number;
  }>>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState('');
  const [sortBy, setSortBy] = React.useState<'name' | 'total'>('total');

  const loadRows = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');
      let page = 1;
      const limit = 200;
      let total = 0;
      const allRows: Array<{
        id: number;
        username: string;
        total_batiments: number;
        total_vehicules: number;
        total_personnel: number;
        total_interventions: number;
      }> = [];
      do {
        const response = await fetch(`${getApiBaseUrl(true)}/panel/v1/moderation/possessions?page=${page}&limit=${limit}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        if (!response.ok || !data?.success) throw new Error(data?.error || 'Erreur chargement possessions');
        const pageRows = Array.isArray(data.data) ? data.data : [];
        allRows.push(...pageRows);
        total = Number(data?.pagination?.total || pageRows.length || 0);
        page += 1;
        if (pageRows.length === 0) break;
      } while (allRows.length < total);
      setRows(allRows);
    } catch (e: any) {
      setError(e?.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const filteredRows = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q
      ? rows.filter((r) => `${r.username} ${r.id}`.toLowerCase().includes(q))
      : rows;
    return [...base].sort((a, b) => {
      if (sortBy === 'name') return a.username.localeCompare(b.username, 'fr');
      const totalA = (a.total_batiments || 0) + (a.total_vehicules || 0) + (a.total_personnel || 0) + (a.total_interventions || 0);
      const totalB = (b.total_batiments || 0) + (b.total_vehicules || 0) + (b.total_personnel || 0) + (b.total_interventions || 0);
      return totalB - totalA;
    });
  }, [rows, search, sortBy]);

  return (
    <div className="rounded-xl border border-[#223046]/90 bg-[#0e1522]/75 p-4 backdrop-blur-sm">
      <h2 className="mb-3 text-sm font-semibold text-cockpit-text">Gestion des possessions des joueurs</h2>
      <div className="space-y-3">
        <p className="text-xs text-cockpit-text-2">Consulte et gère tous les bâtiments, véhicules, personnels et interventions.</p>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_auto_auto]">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un joueur..."
            className="rounded-lg border border-[#223046]/90 bg-[#0e1522]/75 px-3 py-1.5 text-sm text-cockpit-text placeholder:text-cockpit-muted backdrop-blur-sm focus:outline-none"
          />
          <button
            type="button"
            onClick={() => setSearch('')}
            className="rounded-lg border border-cockpit-line/60 bg-cockpit-bg-2/30 px-3 py-1.5 text-xs font-medium text-cockpit-text transition-colors hover:bg-cockpit-bg-2/45"
          >
            Tous les joueurs
          </button>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'name' | 'total')}
            className="rounded-lg border border-[#223046]/90 bg-[#0e1522]/75 px-3 py-1.5 text-xs text-cockpit-text backdrop-blur-sm"
          >
            <option value="total">Trier par total possessions</option>
            <option value="name">Trier par nom</option>
          </select>
        </div>
        {loading && <p className="text-cockpit-text-2">Chargement...</p>}
        {error && <p className="text-red-300">{error}</p>}
        {!loading && !error && (
          <>
            {filteredRows.length === 0 && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
                Aucun profil avec données de jeu trouvé pour la modération.
              </div>
            )}
            <div className="grid grid-cols-1 gap-2 lg:grid-cols-3 xl:grid-cols-4">
              {filteredRows.map((row) => (
                <div key={row.id} className="rounded-xl border border-[#223046]/70 bg-[#0e1522]/60 p-2.5 backdrop-blur-sm">
                  <div className="mb-1.5 flex items-center justify-between">
                    <div className="text-sm font-semibold text-cockpit-text">{row.username}</div>
                    <span className="rounded-md border border-indigo-500/40 bg-indigo-500/15 px-1.5 py-0.5 text-[10px] text-indigo-100">
                      Niv.&nbsp;{row.total_interventions > 100 ? 5 : row.total_interventions > 50 ? 4 : row.total_interventions > 20 ? 3 : 1}
                    </span>
                  </div>
                  <div className="mb-2 flex items-center justify-between text-[10px] text-cockpit-text-2">
                    <span>ID: {row.id}</span>
                    <span>{Number(row.argents || 0).toLocaleString('fr-FR')} €</span>
                    <span>{Number(row.medailles || 0).toLocaleString('fr-FR')} méd.</span>
                  </div>
                  <div className="grid grid-cols-3 gap-1 text-[10px]">
                    <div className="rounded-lg border border-[#223046]/70 bg-[#0e1522]/60 px-1 py-1 text-center">
                      <div className="text-cockpit-text">{row.total_batiments}</div>
                      <div className="text-cockpit-muted">Bâtiments</div>
                    </div>
                    <div className="rounded-lg border border-[#223046]/70 bg-[#0e1522]/60 px-1 py-1 text-center">
                      <div className="text-cockpit-text">{row.total_vehicules}</div>
                      <div className="text-cockpit-muted">Véhicules</div>
                    </div>
                    <div className="rounded-lg border border-[#223046]/70 bg-[#0e1522]/60 px-1 py-1 text-center">
                      <div className="text-cockpit-text">{row.total_personnel}</div>
                      <div className="text-cockpit-muted">Personnel</div>
                    </div>
                  </div>
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={() => navigate(`/panel/moderation/manage/${row.id}`)}
                      className="w-full rounded-lg border border-violet-500/40 bg-violet-500/15 px-2 py-1 text-[10px] font-medium text-violet-100 transition-colors hover:bg-violet-500/25"
                    >
                      Gérer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PanelModerationV1;
